import {
  DASHBOARD_ANALYTICS_EVENTS,
  ENTITY_TYPES,
  MPARTICLE_EVENTS,
  TOGGLE_TARGETS,
} from 'constants/analytics';
import { logEvent } from 'helpers/mparticle';
import type { UserType } from 'types/user';

/**
 * Centralized mParticle analytics for auth, orders, and campaigns.
 *
 * Every track* function delegates to the no-op-safe `logEvent` wrapper, so callers
 * never need to guard for the SDK being unavailable. Events must only be invoked on
 * confirmed API success unless the data plan explicitly requires failure tracking
 * (refunds and campaigns emit `outcome: 'failed'`).
 */

export type UserRole = 'operator' | 'admin';
export type LoginMethod = 'email_password' | 'sso' | 'google';

type AdminLike = Pick<UserType, 'is_chownow_admin' | 'is_region_admin' | 'is_super_admin'>;

export function resolveUserRole(user: Partial<AdminLike> | null | undefined): UserRole {
  if (!user) return 'operator';
  if (user.is_chownow_admin || user.is_super_admin || user.is_region_admin) return 'admin';
  return 'operator';
}

export function trackUserLoggedIn(params: { loginMethod: LoginMethod; userRole: UserRole }): void {
  logEvent(DASHBOARD_ANALYTICS_EVENTS.userLoggedIn, {
    login_method: params.loginMethod,
    user_role: params.userRole,
  });
}

export function trackUserLoggedOut(params: { userRole: UserRole }): void {
  logEvent(DASHBOARD_ANALYTICS_EVENTS.userLoggedOut, {
    user_role: params.userRole,
  });
}

export type OrdersAvailabilityStatus = 'active' | 'paused';

export function trackOrdersAvailabilityToggled(params: { status: OrdersAvailabilityStatus }): void {
  logEvent(MPARTICLE_EVENTS.toggle, {
    toggle_target: TOGGLE_TARGETS.ordersAvailability,
    status: params.status,
  });
}

export type RefundType = 'full' | 'by_item' | 'by_amount';
export type EventOutcome = 'success' | 'failed';

export function trackOrderRefundInitiated(params: {
  refundType: RefundType;
  orderId: string;
  outcome: EventOutcome;
}): void {
  logEvent(MPARTICLE_EVENTS.entityAction, {
    entity_type: ENTITY_TYPES.order,
    action: 'refund_initiated',
    refund_type: params.refundType,
    order_id: params.orderId,
    outcome: params.outcome,
  });
}

export type CampaignType = 'one_time' | 'automated';
export type CampaignChannel = 'email' | 'sms' | 'push';
export type CampaignSendTiming = 'immediate' | 'scheduled';

export function trackCampaignSent(params: {
  campaignType: CampaignType;
  channel: CampaignChannel;
  sendTiming: CampaignSendTiming;
  outcome: EventOutcome;
}): void {
  logEvent(MPARTICLE_EVENTS.contentSaved, {
    content_type: 'campaign',
    campaign_type: params.campaignType,
    channel: params.channel,
    send_timing: params.sendTiming,
    outcome: params.outcome,
  });
}
